/**
 * Sprint 73 — Cómo maneja el negocio el impuesto: incluido en los precios, o calculado aparte.
 *
 * ## Por qué existe
 *
 * La dueña capturó sus precios **con el impuesto ya adentro** —es lo normal en un restaurante: el
 * menú dice $185 y el cliente paga $185— y el sistema tenía configurado `impuesto_porcentaje = 16`,
 * así que le **sumaba 16% encima**: su Espada Individual de $185 se cobraba en $214.60.
 *
 * Sus palabras a través de José: *"el IVA ya está incluido en los precios, ya no es necesario
 * calcularlo y menos imprimirlo; en su lugar debe llevar un 'Los impuestos ya están incluidos'"*.
 *
 * Y la decisión de diseño es de José, y es la correcta: **no se cablea, se configura.** Habrá
 * negocios que sí necesiten desglosarlo, y el que factura lo necesita por ley.
 *
 * ## Las dos formas
 *
 *  - **Incluido** (lo de Asados Las Espadas): el precio es lo que se paga. El impuesto del pedido
 *    queda en 0 y el ticket lleva la leyenda. Es el default para un negocio nuevo, porque es lo que
 *    hace cualquier restaurante que no factura.
 *  - **Calculado**: se suma el porcentaje sobre el subtotal, como hasta hoy.
 *
 * Cambiar la forma **no toca los pedidos ya cobrados**: cada uno guardó su impuesto en su fila. Sólo
 * cambia de aquí en adelante — que es lo único honesto con un histórico.
 */

/** Clave en `ConfiguracionSistema`. */
export const CLAVE_IMPUESTO_INCLUIDO = 'impuesto_incluido';
export const CLAVE_IMPUESTO_PORCENTAJE = 'impuesto_porcentaje';

/**
 * Lee la configuración y devuelve el porcentaje que hay que **sumar** al subtotal.
 *
 * Con el impuesto incluido en los precios, la respuesta es 0: el precio ya lo trae, y sumarlo otra
 * vez es cobrarlo dos veces. Es la línea que estaba cobrándole 16% de más a cada ticket.
 */
export function porcentajeASumar(cfg: {
  incluido: string | null | undefined;
  porcentaje: string | null | undefined;
}): number {
  if (impuestoEstaIncluido(cfg.incluido)) return 0;
  const n = Number(cfg.porcentaje ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * ¿El precio ya trae el impuesto?
 *
 * **El default es `true`**, y es una decisión: un negocio recién instalado que no ha configurado
 * nada tiene precios de menú, no precios sin impuesto. El error de cobrar de más es peor que el de
 * cobrar de menos —el cliente lo reclama en la caja— y de los dos, éste es el que ya pasó.
 */
export function impuestoEstaIncluido(valor: string | null | undefined): boolean {
  if (valor === null || valor === undefined || valor === '') return true;
  return valor === 'true' || valor === '1';
}

/**
 * La leyenda del ticket cuando el impuesto va incluido. Literal de lo que pidió la dueña.
 */
export const LEYENDA_IMPUESTO_INCLUIDO = 'Los impuestos ya están incluidos';
